<template>
    <b-overlay>
        <div class="row">
            <div class="col-md-4 mb-1">
                <v-stats-box :value="summaryData.totalSources" text="Toplam Kaynak Sayısı" icon="assessment" variant="primary" />
            </div>
            <div class="col-md-4 mb-1">
                <v-stats-box :value="summaryData.totalTransactions" text="Toplam İşlem" icon="summarize" variant="info" />
            </div>
            <div class="col-md-4 mb-1">
                <v-stats-box :value="summaryData.grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })" text="Genel Toplam Litre" icon="local_gas_station" variant="success" />
            </div>
        </div>
        <div class="d-flex align-items-center mb-1 px-25">
            <feather-icon icon="FilterIcon" size="13" class="text-muted mr-50" />
            <small class="text-muted">
                <strong>Uygulanan Tarih Aralığı:</strong>
                {{ appliedFilters.startDate ? formatDate(appliedFilters.startDate) : 'Başlangıç tarihi yok' }}
                —
                {{ appliedFilters.endDate ? formatDate(appliedFilters.endDate) : 'Bitiş tarihi yok' }}
                <span v-if="!appliedFilters.startDate && !appliedFilters.endDate" class="text-warning font-weight-bold ml-25">(Tüm tarihler gösterilmektedir)</span>
            </small>
        </div>
        <b-card no-body>
            <v-table ref="reportTable" :items-provider="fetchReport" :editor="processReportData" :fields="reportFields" :busy.sync="reportLoading" pagination="client" search>
                <template #filters>
                    <div class="row align-items-end mb-0">
                        <div class="col-md-3 col-6 mb-1">
                            <label class="form-label">Başlangıç Tarihi</label>
                            <v-date-picker v-model="reportFilters.startDate" placeholder="Başlangıç" />
                        </div>
                        <div class="col-md-3 col-6 mb-1">
                            <label class="form-label">Bitiş Tarihi</label>
                            <v-date-picker v-model="reportFilters.endDate" placeholder="Bitiş" />
                        </div>
                        <div class="col-md-6 col-12 mb-1 d-flex justify-content-end align-items-end">
                            <button class="btn btn-primary" @click="applyFilter" :disabled="reportLoading">
                                <feather-icon icon="SearchIcon" size="14" class="mr-50" />
                                Filtrele
                            </button>
                            <button class="btn btn-outline-secondary ml-50" @click="clearFilters">
                                <feather-icon icon="XIcon" size="14" class="mr-50" />
                                Temizle
                            </button>
                        </div>
                    </div>
                </template>
                <template #additional-actions>
                    <div class="d-flex justify-content-end">
                        <button v-b-tooltip.hover.bottom title="Dışa Aktar" class="btn btn-sm btn-icon rounded-circle btn-outline-success p-25" v-on:click="exportToExcel" :disabled="reportLoading">
                            <feather-icon icon="ArrowDownIcon" size="12" />
                        </button>
                    </div>
                </template>
            </v-table>
        </b-card>
    </b-overlay>
</template>

<script>
import api from "@/api/fuel-storage";
import { BCard, BOverlay, VBTooltip } from "bootstrap-vue";
import VStatsBox from "@/components/VStatsBox.vue";
import xlsx from "json-as-xlsx";

const getInitialFilters = () => ({
    startDate: null,
    endDate: null,
});

export default {
    name: "FuelConsumptionCompanyTotalView",
    components: { BCard, VStatsBox, BOverlay },
    directives: {
        "b-tooltip": VBTooltip,
    },
    data() {
        return {
            summaryData: {
                totalSources: 0,
                totalTransactions: 0,
                grandTotal: 0,
            },
            reportFilters: getInitialFilters(),
            appliedFilters: getInitialFilters(),
            reportLoading: false,
            reportFields: [
                { key: "siraNo", name: "S.No", thClass: "text-nowrap", tdClass: "text-nowrap" },
                { key: "Source", name: "Kaynak", thClass: "text-nowrap", tdClass: "text-nowrap" },
                { key: "MotorinLtStr", name: "Motorin [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "AdblueLtStr", name: "AdBlue [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "OtherLtStr", name: "Diğer [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "TotalLtStr", name: "Toplam [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right font-weight-bold" },
                { key: "TransactionCount", name: "İşlem Sayısı", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "TotalCostExclTaxStr", name: "Tutar KDV Hariç (₺)", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "TotalCostInclTaxStr", name: "Tutar KDV Dahil (₺)", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
            ],
        };
    },
    methods: {
        formatDate(dateStr) {
            if (!dateStr) return "-";
            const [year, month, day] = dateStr.split("-");
            return `${day}.${month}.${year}`;
        },
        applyFilter() {
            this.$refs.reportTable.refresh();
        },
        exportToExcel() {
            const table = this.$refs.reportTable;
            const tableData = table?.$data?.d_items || [];
            if (tableData.length === 0) {
                this.$toast.warning("Dışa aktarılacak veri bulunamadı");
                return;
            }
            this.generateExcelFile(tableData);
        },
        generateExcelFile(data) {
            const columns = [
                { label: "S.No", value: "siraNo" },
                { label: "Kaynak", value: "Source" },
                { label: "Motorin [lt]", value: "MotorinLtStr" },
                { label: "AdBlue [lt]", value: "AdblueLtStr" },
                { label: "Diğer [lt]", value: "OtherLtStr" },
                { label: "Toplam [lt]", value: "TotalLtStr" },
                { label: "İşlem Sayısı", value: "TransactionCount" },
                { label: "Tutar KDV Hariç (₺)", value: "TotalCostExclTaxStr" },
                { label: "Tutar KDV Dahil (₺)", value: "TotalCostInclTaxStr" },
            ];
            const content = data.map((item) => ({
                siraNo: item.siraNo || "-",
                Source: item.Source || "-",
                MotorinLtStr: item.MotorinLtStr || "0",
                AdblueLtStr: item.AdblueLtStr || "0",
                OtherLtStr: item.OtherLtStr || "0",
                TotalLtStr: item.TotalLtStr || "0",
                TransactionCount: item.TransactionCount || 0,
                TotalCostExclTaxStr: item.TotalCostExclTaxStr || "0",
                TotalCostInclTaxStr: item.TotalCostInclTaxStr || "0",
            }));
            const settings = {
                fileName: `Sirket_Geneli_Yakit_Alimlari_${new Date().toISOString().slice(0, 10)}`,
                extraLength: 3,
                writeMode: "writeFile",
            };
            xlsx([{ sheet: "Şirket Geneli Yakıt Alımları", columns, content }], settings);
        },
        clearFilters() {
            this.reportFilters = getInitialFilters();
            this.$nextTick(() => this.$refs.reportTable.refresh());
        },
        fetchReport() {
            this.appliedFilters = { ...this.reportFilters };
            const params = {
                ...this.reportFilters,
                startDate: this.reportFilters.startDate ? `${this.reportFilters.startDate}T00:00:00` : null,
                endDate: this.reportFilters.endDate ? `${this.reportFilters.endDate}T23:59:59` : null,
            };
            return api.FuelConsumptionReport.GetCompanyTotal(params);
        },
        processReportData(response) {
            if (!response?.Success) {
                this.$result({ data: response });
                this.summaryData = { totalSources: 0, totalTransactions: 0, grandTotal: 0 };
                return [];
            }
            const data = response.Data || [];
            this.summaryData = {
                totalSources: data.length,
                totalTransactions: data.reduce((sum, item) => sum + (item.TransactionCount || 0), 0),
                grandTotal: data.reduce((sum, item) => sum + (item.TotalLt || 0), 0),
            };
            return data.map((item, index) => ({
                ...item,
                siraNo: index + 1,
            }));
        },
    },
};
</script>
